import type React from 'react';
import { useWindowWidth } from '../hooks/useWindowWidth';

const BLUE = '#2563EB';
const INDIGO = '#4338CA';
const HEADING = '#0F172A';
const MUTED = '#64748B';
const BORDER = '#E2E8F0';
const PAGE_BG = '#F8FAFC';
const PANEL_BG = '#EFF6FF';

interface AuthShellProps {
  headline: string;
  subtext: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export default function AuthShell({
  headline,
  subtext,
  children,
  footer,
}: AuthShellProps): JSX.Element {
  const mobile = useWindowWidth() < 900;

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: PAGE_BG,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <LeftColumn headline={headline} subtext={subtext} footer={footer}>
        {children}
      </LeftColumn>
      {!mobile && <BenefitsPanel />}
    </div>
  );
}

function LeftColumn({ headline, subtext, children, footer }: AuthShellProps): JSX.Element {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={{ padding: '28px 32px 0' }}>
        <img src="/assets/sydon-icon.png" alt="Sydon" style={{ height: 34, width: 'auto' }} />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: HEADING }}>{headline}</div>
            <div style={{ fontSize: 13.5, color: MUTED, marginTop: 6 }}>{subtext}</div>
          </div>

          <div
            style={{
              background: '#fff',
              border: `1px solid ${BORDER}`,
              borderRadius: 24,
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)',
              padding: 28,
              boxSizing: 'border-box',
            }}
          >
            {children}
          </div>

          {footer && <div style={{ textAlign: 'center', marginTop: 18 }}>{footer}</div>}
        </div>
      </div>

      <div style={{ padding: '18px 0 24px', textAlign: 'center', fontSize: 12, color: '#94A3B8' }}>
        © Sydon 2026 ·{' '}
        <a
          href="https://sydon.ai/terms-of-service"
          target="_blank"
          rel="noreferrer"
          style={{ color: MUTED, fontWeight: 600, textDecoration: 'none' }}
        >
          Terms &amp; Conditions
        </a>
      </div>
    </div>
  );
}

interface Benefit {
  title: string;
  subtitle: string;
  color: string;
  Icon: () => JSX.Element;
}

const BENEFITS: Benefit[] = [
  {
    title: '25–35% Revenue Growth',
    subtitle: 'Intelligent pricing & listing optimization',
    color: BLUE,
    Icon: TrendingUpIcon,
  },
  {
    title: 'Account Protection',
    subtitle: '24/7 monitoring & instant risk alerts',
    color: INDIGO,
    Icon: ShieldIcon,
  },
  {
    title: 'Team Collaboration',
    subtitle: 'Multi-user organization management',
    color: BLUE,
    Icon: UsersIcon,
  },
];

function BenefitsPanel(): JSX.Element {
  return (
    <div
      style={{
        flex: 1,
        background: PANEL_BG,
        borderLeft: `1px solid ${BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 48,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: 380, textAlign: 'center' }}>
        <img
          src="/assets/sydon-icon.png"
          alt="Sydon"
          style={{ height: 44, width: 'auto', marginBottom: 20 }}
        />
        <div style={{ fontSize: 24, fontWeight: 800, color: HEADING, marginBottom: 10 }}>
          Your Amazon Business, Automated
        </div>
        <div style={{ fontSize: 13.5, color: MUTED, marginBottom: 28, lineHeight: 1.5 }}>
          AI-powered agents that grow your revenue, protect your account, and optimize your
          operations — 24/7.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {BENEFITS.map((b) => (
            <BenefitRow key={b.title} benefit={b} />
          ))}
        </div>

        <RatingBlock />
      </div>
    </div>
  );
}

function BenefitRow({ benefit }: { benefit: Benefit }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        background: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 16,
        padding: '14px 16px',
        textAlign: 'left',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: benefit.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <benefit.Icon />
      </div>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: HEADING }}>{benefit.title}</div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{benefit.subtitle}</div>
      </div>
    </div>
  );
}

const STAR_KEYS = ['star-1', 'star-2', 'star-3', 'star-4', 'star-5'];

function RatingBlock(): JSX.Element {
  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
        Trusted by 1,000+ Amazon sellers
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginBottom: 6 }}>
        {STAR_KEYS.map((key) => (
          <StarIcon key={key} />
        ))}
      </div>
      <div style={{ fontSize: 12, color: MUTED }}>4.9 / 5 average rating</div>
    </div>
  );
}

function TrendingUpIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}

function ShieldIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function UsersIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function StarIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 20 20" fill="#FBBF24">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}
